/* Where the cohort is in the quarter.
 *
 * Pure date arithmetic, no React, so it can be tested on its own — which
 * matters more here than usual, because a date bug is invisible until the day
 * it is wrong, and by then the term has moved on.
 *
 * Dates are parsed as LOCAL midnight rather than by `new Date('2026-09-28')`,
 * which is parsed as UTC and lands on the previous evening in any negative
 * offset — an off-by-one that would show the wrong week for part of every day.
 */

const DAY = 86400000;

const localMidnight = iso => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const startOfDay = d => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/**
 * @returns {{phase:'before'|'during'|'after', week?:number, totalWeeks?:number,
 *            daysLeft?:number, daysUntil?:number, start:Date, end:Date}}
 */
export function termState(term, totalWeeks, now = new Date()) {
  const start = localMidnight(term.start);
  const end = localMidnight(term.end);
  const today = startOfDay(now);

  if (today < start) {
    return { phase: 'before', daysUntil: Math.round((start - today) / DAY), start, end };
  }
  if (today > end) {
    return { phase: 'after', start, end };
  }
  /* Clamped: the quarter runs a few days past the eleventh week, and those
     days belong to week 11 — the final checkpoint — not to a twelfth. */
  const week = Math.min(totalWeeks, Math.floor((today - start) / (7 * DAY)) + 1);
  return { phase: 'during', week, totalWeeks, daysLeft: Math.round((end - today) / DAY), start, end };
}

/** The unit running on a given day, by the course's start dates (D-058).

    The last unit whose `starts` is on or before today; before the first start,
    the first unit. Units carry `starts` as 'YYYY-MM-DD'; one without it is
    skipped. Used to highlight one module at a time while progress tracking is
    off — with progress on, the highlight follows the student instead. */
export function unitOnDate(units, now = new Date()) {
  const today = startOfDay(now);
  let current = units[0]?.id ?? null;
  for (const u of units) if (u.starts && localMidnight(u.starts) <= today) current = u.id;
  return current;
}

/** "Sep 28" — short, for a panel with no room for more. */
export const shortDate = d =>
  d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
